import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { getLocations, createLocation, updateLocation, deleteLocation, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill, BtnPrimary, BtnGhost, Field, inputCls, areaCls } from "./_ui";

const EMPTY = { area: "", slug: "", title: "", intro: "", status: "draft" };
const kebab = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const SUGGEST = ["Whitefield", "Jayanagar", "Malleshwaram", "HSR Layout", "Yelahanka", "Indiranagar", "Rajajinagar", "JP Nagar", "Koramangala", "Electronic City"];

function LocForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.area.trim() || !f.title.trim()) return toast.error("Area and title are required");
    const body = { area: f.area.trim(), title: f.title.trim(), intro: f.intro, status: f.status };
    setBusy(true);
    try {
      if (f.id) await updateLocation(f.id, body);
      else await createLocation({ ...body, slug: f.slug.trim() || kebab(f.area) });
      toast.success(f.id ? "Location page updated" : "Location page created");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={f.id ? "Edit location page" : "New location page"} onClose={onClose} testid="loc-form-modal"
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary onClick={save} disabled={busy} data-testid="loc-form-save">{busy ? "Saving…" : "Save"}</BtnPrimary></>}>
      <div className="grid grid-cols-1 gap-4">
        <Field label="Area / locality">
          <input value={f.area} onChange={set("area")} data-testid="loc-field-area" list="area-suggest" placeholder="e.g. Whitefield" className={inputCls} />
          <datalist id="area-suggest">{SUGGEST.map((a) => <option key={a} value={a} />)}</datalist>
        </Field>
        {!f.id && <Field label="Slug (auto if blank)"><input value={f.slug} onChange={set("slug")} placeholder={kebab(f.area) ? `purohith-in-${kebab(f.area)}` : ""} className={inputCls} /></Field>}
        <Field label="Page title"><input value={f.title} onChange={set("title")} data-testid="loc-field-title" placeholder="Book a Purohith in Whitefield, Bengaluru" className={inputCls} /></Field>
        <Field label="Intro copy"><textarea rows={4} value={f.intro} onChange={set("intro")} className={areaCls} /></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} className={inputCls}><option value="draft">Draft</option><option value="published">Published</option></select></Field>
      </div>
    </Modal>
  );
}

export default function LocationPages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = () => getLocations().then(setRows).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };

  const published = rows.filter((r) => r.status === "published").length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-location-pages">
      <PageHead eyebrow="Local SEO" title="Location pages"
        desc="Create area-specific landing pages for Bengaluru localities to capture 'purohith near me' searches."
        action={<BtnPrimary onClick={() => setForm(EMPTY)} data-testid="loc-add-btn" className="inline-flex items-center gap-1.5"><Plus size={16} /> New location</BtnPrimary>} />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={MapPin} label="Location pages" value={rows.length} />
        <Stat tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Published" value={published} />
        <Stat tint={{ bg: "#FBF3E7", fg: "#B98A2E" }} label="Draft" value={rows.length - published} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((l) => (
          <div key={l.id} data-testid={`loc-card-${l.id}`} className="rounded-2xl border border-[#E9E2D6] bg-white p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2"><MapPin size={16} className="text-[#D84315]" /><h3 className="font-heading text-base font-semibold text-[#2C241B]">{l.area}</h3></div>
              <Pill status={l.status} />
            </div>
            <p className="mt-2 font-body text-sm font-medium text-[#2C241B]">{l.title}</p>
            <p className="mt-1 font-body text-xs text-[#A79C8A]">/{l.slug}</p>
            {l.intro && <p className="mt-2 line-clamp-2 font-body text-xs text-[#8A8072]">{l.intro}</p>}
            <div className="mt-3 flex gap-2">
              <button onClick={() => setForm({ ...EMPTY, ...l })} data-testid={`loc-edit-${l.id}`} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]"><Pencil size={13} /> Edit</button>
              <button onClick={() => act(() => updateLocation(l.id, { status: l.status === "published" ? "draft" : "published" }), l.status === "published" ? "Unpublished" : "Published")} className="rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]">{l.status === "published" ? "Unpublish" : "Publish"}</button>
              <button onClick={() => { if (window.confirm(`Delete ${l.area} page?`)) act(() => deleteLocation(l.id), "Location page deleted"); }} data-testid={`loc-delete-${l.id}`} className="inline-flex items-center gap-1 rounded-lg bg-[#FDECEA] px-2.5 py-1.5 font-body text-xs font-semibold text-[#C62828] hover:bg-[#f8dcd8]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {!loading && rows.length === 0 && <div className="mt-6"><Empty>No location pages yet — add your first Bengaluru area.</Empty></div>}

      {form && <LocForm initial={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
